/* eslint-disable no-unused-expressions */
/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import JSCookies from 'js-cookie';
import Heading from '@havenengineering/module-shared-library/dist/components/Heading';
import Grid from '@havenengineering/module-shared-library/dist/components/Grid';
import { useRouter } from 'next/router';
import { usePathname } from 'next/navigation';
import { stringifyUrl } from 'query-string';
import styles from './section.module.scss';
import { Query } from '../../../../types/v2/holiday-search';
import { AggregatingResultCardContainer } from '../aggreagating-result-card-container';
import { useAccommodationGallery } from '../../../shared/hook/use-accommodation-gallery/use-accommodation-gallery';
import { AccommodationGallery } from '../../../shared/component/accommodation-gallery';
import { GalleryItem } from '../../../../types/shared';
import { AggregatedAccommodationType } from '../../type/types';
import {
  handleCardWithOccupancyIndicator,
  handleSelectItemPartnerPricing,
  setPageData,
} from '../../../../helpers/gtm/gtm-aggregating-journey';
import { debounceDataLayerEvent } from '../../../../helpers/gtm/analytics';
import { useHashing } from '../../../shared/hook/use-hashing/use-hashing';
import { useAppLoading } from '../../../../providers/appLoadingProvider';
import { isAccommodationTypeSoldOutBasedOnReservedExperiment } from '../../../shared/helper/show-hide-reserved-breaks';
import NoResults from '../../../shared/component/no-results/no-results';
import SearchSatisfaction from '../../../shared/component/search-satisfaction';
import { SingleColumnSectionCard } from '../../../aggregating-by-park/holidays/component/single-column-section-card/single-column-section-card';
import { SelectButton } from '../../../shared/component/select-button/select-button';
import { getNextStepPathname, getReferringStepDetails } from '../../../../helpers/redirects';
import { useJourneySteps } from '../../../../hooks/useJourneySteps';
import { getAffiliatePromoCookie } from '../../../../helpers/affiliatePromoCookie';
import { AFFILIATE_COOKIE_NAME } from '../../../../constant';
import { useSelection } from '../../../shared/hook/use-selection/use-selection';

type Props = {
  resultsByAccommodationType: AggregatedAccommodationType[];
  query: Query;
  dataLayerPageName: string;
  accommodationTypeOrder: string | null;
  bannerText: string | null;
  showDogFriendly?: boolean;
  interactionProps?: string;
};

const RECOMMENDED_GRADE = 'Bronze';

export const Section = ({
  resultsByAccommodationType,
  query,
  dataLayerPageName,
  accommodationTypeOrder,
  bannerText,
  showDogFriendly = true,
  interactionProps,
}: Props) => {
  const journeySteps = useJourneySteps(undefined, query);
  const pathName = usePathname();
  const [queryObject, setQueryObject] = useState({});
  const { selection, setSelection } = useSelection();
  const { setIsAppLoading } = useAppLoading();

  useEffect(() => {
    setTimeout(() => {
      setIsAppLoading(false);
    }, 200);
  }, [resultsByAccommodationType]);

  useEffect(() => {
    const interaction = [
      `${accommodationTypeOrder ? `accommodationTypeOrder:${accommodationTypeOrder}` : ''}`,
      interactionProps || [],
    ];

    debounceDataLayerEvent(() =>
      setPageData(
        query,
        resultsByAccommodationType,
        dataLayerPageName,
        'grade',
        null,
        interaction.join(','),
        !!resultsByAccommodationType.length,
        bannerText,
      ),
    );
  }, [bannerText]);

  const router = useRouter();

  const { handleHashing } = useHashing();

  const {
    handleAccommodationGalleryGeneral,
    resultToShowInGalleryGeneral,
    activeTabStateGeneral,
    showAccommodationGalleryGeneral,
    setShowAccommodationGalleryGeneral,
  } = useAccommodationGallery();

  useEffect(() => {
    showAccommodationGalleryGeneral && document.body.classList.add(`accommodationGalleryOpen`);
    !showAccommodationGalleryGeneral && document.body.classList.remove(`accommodationGalleryOpen`);

    return () => {
      document.body.classList.remove(`accommodationGalleryOpen`);
    };
  }, [showAccommodationGalleryGeneral]);

  useEffect(() => {
    const handleRouteChange = () => {
      if (window.location.hash !== '#accommodationGallery') {
        setShowAccommodationGalleryGeneral?.(false);
      }
    };

    if (handleHashing) {
      showAccommodationGalleryGeneral && handleHashing('accommodationGallery');
    }

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [showAccommodationGalleryGeneral]);

  const dogs = query.dogs ? parseInt(query.dogs as string, 10) : 0;

  const promoCookie = JSCookies.get(AFFILIATE_COOKIE_NAME);

  const { affiliatePromoCode } = getAffiliatePromoCookie(query.wacampaign, promoCookie, true);

  const onSelectGrade = () => {
    if (affiliatePromoCode) {
      handleSelectItemPartnerPricing(
        'accommodation',
        affiliatePromoCode,
        selection.price || 0,
        selection.discountedPrice || 0,
      );
    }

    handleCardWithOccupancyIndicator(
      selection.selectionName?.toString() || '',
      selection.occupancy?.totalOccupancy || 0,
      selection.occupancy?.totalUnitsRemaining || 0,
      selection.occupancy?.availabilityLabel || '',
    );
  };

  const url = getNextStepPathname(journeySteps, pathName) || '/';

  const { newReferringStepName } = getReferringStepDetails(
    journeySteps,
    pathName,
    query.referringStep,
  );

  useEffect(() => {
    const searchData = {
      ...query,
      referringStep: newReferringStepName,
      selectedGradeCode: selection.selection,
      selectedGradeName: selection.selectionName,
    };

    setQueryObject(searchData);
  }, [selection.selection]);

  return (
    <>
      <Grid.Col className={styles.gradeResultContent} md={12} lg={6}>
        {resultsByAccommodationType?.length > 0 ? (
          <>
            <SingleColumnSectionCard>
              {resultsByAccommodationType?.map((result: AggregatedAccommodationType) => {
                return (
                  <div key={result.accommodationType}>
                    <Heading className={styles.gradeHeader}>{result.accommodationType}</Heading>
                    <Grid.Row gutter={24}>
                      {result?.subItems?.map((item: AggregatedAccommodationType, index: number) => {
                        const { accommodationDescription, leadingPrice, available } = item;
                        const isDogFriendly = accommodationDescription.includes('Dog Friendly');

                        if (!showDogFriendly && isDogFriendly) {
                          return null;
                        }

                        const isRecommended =
                          accommodationDescription.includes(RECOMMENDED_GRADE) &&
                          ((dogs === 0 && !isDogFriendly) || (dogs > 0 && isDogFriendly));

                        const isSoldOut = isAccommodationTypeSoldOutBasedOnReservedExperiment(
                          true,
                          item,
                        );

                        const isActive = selection.selection === item.baseGradeCode;
                        return (
                          (leadingPrice || !available) && (
                            <Grid.Col xs={12} sm={12} md={12} xl={12} key={index} gutter={24}>
                              <AggregatingResultCardContainer
                                item={item}
                                HandleAccommodationGalleryGeneral={
                                  handleAccommodationGalleryGeneral
                                }
                                query={query}
                                isSoldOut={isSoldOut}
                                recommended={isRecommended && !isSoldOut}
                                setSelection={setSelection}
                                selectedGrade={selection.selection || ''}
                                isActive={isActive}
                              />
                            </Grid.Col>
                          )
                        );
                      })}
                    </Grid.Row>
                  </div>
                );
              })}
            </SingleColumnSectionCard>
            <SelectButton
              active={!selection.soldOut && selection.selection !== ''}
              label={
                selection.selection !== '' && selection.selectionName
                  ? `Select ${selection.selectionName}`
                  : 'Select'
              }
              url={stringifyUrl({
                url,
                query: queryObject,
              })}
              onClick={onSelectGrade}
            />
          </>
        ) : (
          <NoResults query={query} activeStep="Accommodation" />
        )}
      </Grid.Col>
      {resultsByAccommodationType?.length > 0 && (
        <Grid.Col xs={12}>
          <Grid.Row justify="center">
            <Grid.Col xs={12} lg={6}>
              <SearchSatisfaction />
            </Grid.Col>
          </Grid.Row>
        </Grid.Col>
      )}
      {showAccommodationGalleryGeneral && (
        <AccommodationGallery
          open={showAccommodationGalleryGeneral}
          setGalleryOpen={setShowAccommodationGalleryGeneral}
          activeTab={activeTabStateGeneral}
          backButtonLabel="Back to search"
          resultToShowInGalleryGeneral={resultToShowInGalleryGeneral as GalleryItem}
          withParkInfo={false}
          showFooter
          doneButtonLabel="View holidays"
        />
      )}
    </>
  );
};
